import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"

import type { AiChatMessage } from "@productlane/lib/src/ai"
import { Button, Dialog, useBreakpoints } from "@productlane/ui"
import { DocsProposalCard } from "@productlane/ui/src/components/ai-chat"
import { queries } from "@productlane/zero/queries"

import type {
	DocsProposalPreview,
	GroupProposalContent,
} from "./docs-proposal-previews"
import { ReviewDiff } from "@/screens/app/helpCenter/review-diff"
import { docsProposalPreviews } from "./docs-proposal-previews"

type DocsProposalSuggestion = NonNullable<
	AiChatMessage["docsProposalSuggestion"]
>
type DocsProposalChangeRow = NonNullable<
	DocsProposalSuggestion["changes"]
>[number]

const NO_PREVIEWS = new Map<string, DocsProposalPreview>()

const DocsProposalPreviewDialog = ({
	preview,
	onOpenChange,
	onReview,
}: {
	preview: DocsProposalPreview | null
	onOpenChange: (open: boolean) => void
	onReview: () => void
}) => {
	const { isMobile } = useBreakpoints()

	return (
		<Dialog
			isOpen={preview !== null}
			onOpenChange={onOpenChange}
			size="xl"
			title={preview?.title ?? "Proposed changes"}
			description={
				preview
					? "The live article on the left, the staged change on the right."
					: undefined
			}
			scrollContainerClasses="max-h-[70dvh]"
			footer={
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="rounded-lg"
					>
						Close
					</Button>
					<Button onClick={onReview} className="rounded-lg">
						Review
					</Button>
				</div>
			}
		>
			{preview?.mergedHtml != null && (
				<ReviewDiff
					layout={isMobile ? "flat" : "split"}
					icon={preview.icon}
					articleTitle={preview.title}
					baseHtml={preview.baseHtml}
					mergedHtml={preview.mergedHtml}
					fallback={
						<p className="py-6 text-center text-sm text-secondary">
							This change is too large to pair block by block. Open the review
							screen to read it.
						</p>
					}
				/>
			)}
		</Dialog>
	)
}

// One card per staged change, with the proposed body pulled from the run's
// group so the card reads like the article it would produce.
export const DocsProposalCards = ({
	changes,
	groupId,
	onSkip,
}: {
	changes: DocsProposalChangeRow[]
	groupId?: string
	onSkip?: (proposalId: string) => void
}) => {
	const navigate = useNavigate()
	const [group] = useQuery(queries.proposalGroupById(groupId ?? ""), {
		enabled: Boolean(groupId),
	})
	const [openPreviewId, setOpenPreviewId] = useState<string | null>(null)

	const previews = useMemo(() => {
		const proposals = group?.proposals as
			| readonly GroupProposalContent[]
			| undefined
		return proposals ? docsProposalPreviews(proposals) : NO_PREVIEWS
	}, [group])

	const openPreview = openPreviewId ? previews.get(openPreviewId) : undefined
	const openRoute = changes.find((c) => c.id === openPreviewId)?.route

	return (
		<>
			{changes.map((change) => {
				const preview = previews.get(change.id)
				return (
					<DocsProposalCard
						key={change.id}
						kind={change.kind}
						title={preview?.title ?? change.title}
						html={preview?.mergedHtml ?? null}
						onPreview={
							preview?.mergedHtml != null
								? () => setOpenPreviewId(change.id)
								: undefined
						}
						onOpen={() => void navigate({ href: change.route })}
						onSkip={onSkip ? () => onSkip(change.id) : undefined}
					/>
				)
			})}
			<DocsProposalPreviewDialog
				preview={openPreview ?? null}
				onOpenChange={(open) => {
					if (!open) setOpenPreviewId(null)
				}}
				onReview={() => {
					setOpenPreviewId(null)
					if (openRoute) void navigate({ href: openRoute })
				}}
			/>
		</>
	)
}
